import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LIcon from "@components/LIcon";
import LInput from "@components/LInput";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import AddToPlaylistItem from "@components/modals/AddToPlaylistModal/AddToPlaylistItem";
import { AddToPlaylistModalProps } from "@components/modals/AddToPlaylistModal/types";
import { CreatePlaylistModalSWMSheet } from "@components/modals/CreatePlaylistModalSWM";
import { useLibrary } from "@hooks/trpc/useLibrary";
import { useAddSong, useRemoveSong } from "@hooks/trpc/usePlaylists";
import {
  compareLikedSongsFirst,
  isLikedSongsPlaylist,
  isUserSaveDestination,
} from "@utils/collections/playlists";
import { keyExtractor } from "@utils/data";
import { useOfflineLibrary } from "@utils/downloadManager/hooks";
import { useAuth } from "@utils/firebaseAuth/auth";
import useInternetStatus from "@utils/internetStatus";
import { forwardRef, useEffect, useMemo, useState } from "react";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { useWindowDimensions } from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { theme } from "@theme";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

const COMPACT_SHEET_MAX_HEIGHT = 390;
const COMPACT_BOUNDARY_HEIGHT = theme.spacing.s4 + 1;
const EXPANDED_BOUNDARY_HEIGHT = 56;

type AddToPlaylistModalSWMContentProps = {
  isExpanded?: boolean;
} & AddToPlaylistModalProps;

export const AddToPlaylistModalSWM = ({
  isExpanded = false,
  onSuccess,
  song,
}: AddToPlaylistModalSWMContentProps) => {
  const { firebaseUser: user } = useAuth();
  const {
    isLiked,
    query: { data: library },
    toggleSongLike,
  } = useLibrary();
  const { addSong } = useAddSong();
  const { removeSong } = useRemoveSong({ disableToast: true });
  const { offlineLibrary } = useOfflineLibrary();
  const isOnline = useInternetStatus();
  const bottomSheetManager = useBottomSheetManager();
  const [searchTerm, setSearchTerm] = useState("");
  const searchVisibility = useSharedValue(isExpanded ? 1 : 0);

  const playlists = useMemo(() => {
    const libraryPlaylists = library?.filter(
      (value) => value.type === "playlist",
    );
    const ownedPlaylists = libraryPlaylists?.filter((value) =>
      isUserSaveDestination(value, user?.uid),
    );

    if (isOnline) {
      return ownedPlaylists?.sort(compareLikedSongsFirst);
    }

    const offlinePlaylistIds = new Set(
      offlineLibrary
        ?.filter((item) => item.type === "playlist")
        .map((item) => item.id),
    );

    return ownedPlaylists
      ?.filter(
        (playlist) =>
          isLikedSongsPlaylist(playlist) ||
          offlinePlaylistIds.has(playlist.id),
      )
      .sort(compareLikedSongsFirst);
  }, [isOnline, library, offlineLibrary, user?.uid]);

  const [initialGrouping, setInitialGrouping] = useState<{
    orderedIds: string[];
    selectedIds: Set<string>;
  } | null>(() =>
    playlists
      ? {
          orderedIds: playlists.map((playlist) => playlist.id),
          selectedIds: new Set(
            playlists
              .filter((playlist) => playlist.songIds.includes(song.id))
              .map((playlist) => playlist.id),
          ),
        }
      : null,
  );

  if (!initialGrouping && playlists) {
    setInitialGrouping({
      orderedIds: playlists.map((playlist) => playlist.id),
      selectedIds: new Set(
        playlists
          .filter((playlist) => playlist.songIds.includes(song.id))
          .map((playlist) => playlist.id),
      ),
    });
  }

  const { selectedPlaylists, unselectedPlaylists } = useMemo(() => {
    const currentPlaylists = playlists ?? [];
    const playlistsById = new Map(
      currentPlaylists.map((playlist) => [playlist.id, playlist]),
    );
    const orderedPlaylists = initialGrouping
      ? [
          ...initialGrouping.orderedIds
            .map((id) => playlistsById.get(id))
            .filter((playlist) => playlist !== undefined),
          ...currentPlaylists.filter(
            (playlist) => !initialGrouping.orderedIds.includes(playlist.id),
          ),
        ]
      : currentPlaylists;
    const selected = orderedPlaylists.filter((playlist) =>
      initialGrouping
        ? initialGrouping.selectedIds.has(playlist.id)
        : playlist.songIds.includes(song.id),
    );
    const unselected = orderedPlaylists.filter((playlist) =>
      initialGrouping
        ? !initialGrouping.selectedIds.has(playlist.id)
        : !playlist.songIds.includes(song.id),
    );
    const normalizedSearchTerm = isExpanded
      ? searchTerm.trim().toLocaleLowerCase()
      : "";
    return {
      selectedPlaylists: selected,
      unselectedPlaylists: normalizedSearchTerm
        ? unselected.filter((playlist) =>
            playlist.title.toLocaleLowerCase().includes(normalizedSearchTerm),
          )
        : unselected,
    };
  }, [initialGrouping, isExpanded, playlists, searchTerm, song.id]);

  const hasCompactSeparator =
    selectedPlaylists.length > 0 && unselectedPlaylists.length > 0;

  useEffect(() => {
    searchVisibility.value = withTiming(isExpanded ? 1 : 0, {
      duration: 220,
      easing: Easing.out(Easing.cubic),
    });
  }, [isExpanded, searchVisibility]);

  const boundaryAnimatedStyle = useAnimatedStyle(() => ({
    height: interpolate(
      searchVisibility.value,
      [0, 1],
      [
        hasCompactSeparator ? COMPACT_BOUNDARY_HEIGHT : 0,
        EXPANDED_BOUNDARY_HEIGHT,
      ],
    ),
  }));
  const dividerAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(searchVisibility.value, [0, 0.35], [1, 0]),
  }));
  const searchAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(searchVisibility.value, [0.15, 1], [0, 1]),
    transform: [
      {
        translateY: interpolate(searchVisibility.value, [0, 1], [-6, 0]),
      },
      {
        scale: interpolate(searchVisibility.value, [0, 1], [0.985, 1]),
      },
    ],
  }));

  const handlePressCreatePlaylist = () => {
    bottomSheetManager.open(<CreatePlaylistModalSWMSheet songToAdd={song} />, {
      mode: "push",
    });
  };

  const renderPlaylist = (item: NonNullable<typeof playlists>[number]) => {
    const handlePlaylistSelect = (isSelected: boolean) => {
      if (isLikedSongsPlaylist(item)) {
        if (isSelected !== isLiked({ id: song.id, type: "song" })) {
          toggleSongLike(song);
        }
        return;
      }

      if (isSelected) {
        addSong([item.id], song);
        onSuccess?.();
        return;
      }

      removeSong.mutate({ id: item.id, songId: song.id });
    };

    return (
      <AddToPlaylistItem
        isSelected={item.songIds.includes(song.id)}
        onSelect={handlePlaylistSelect}
        playlist={item}
        sentryEventName="add_to_playlist_item_swm"
        subtitleColor="blackTranslucent70"
        titleColor="black"
      />
    );
  };

  return (
    <LBox flex={1} pb="l1">
      <LBox
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        pb="s4"
        px="s4"
      >
        <LText color="black" variant="listItem">
          Saved in
        </LText>
        {isOnline ? (
          <LPressable
            accessibilityLabel="New playlist"
            accessibilityRole="button"
            hitSlop={8}
            onPress={handlePressCreatePlaylist}
            sentryEventName="add_to_playlist_create_playlist_button_swm"
          >
            <LBox
              alignItems="center"
              backgroundColor="black"
              borderRadius="max"
              px="s2_5"
              py="s1_5"
            >
              <LText color="white" variant="bodyMSemiBold">
                New playlist
              </LText>
            </LBox>
          </LPressable>
        ) : null}
      </LBox>
      <LFlatList
        contentContainerStyle={{ paddingHorizontal: theme.spacing.s4 }}
        data={unselectedPlaylists}
        disableAutomaticPadding
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        keyExtractor={keyExtractor}
        ListEmptyComponent={() =>
          (isExpanded && searchTerm) || !selectedPlaylists.length ? (
            <LText color="blackTranslucent70">
              {isExpanded && searchTerm
                ? "No playlists found"
                : isOnline
                  ? "You currently don't have any playlists"
                  : "You don't have any downloaded playlists available"}
            </LText>
          ) : null
        }
        ListHeaderComponent={
          <>
            {selectedPlaylists.map((playlist) => (
              <LBox key={playlist.id}>{renderPlaylist(playlist)}</LBox>
            ))}
            <Animated.View
              style={[{ overflow: "hidden" }, boundaryAnimatedStyle]}
            >
              {hasCompactSeparator ? (
                <Animated.View
                  style={[
                    {
                      backgroundColor: theme.colors.blackTranslucent10,
                      height: 1,
                      left: 0,
                      position: "absolute",
                      right: 0,
                      top: 0,
                    },
                    dividerAnimatedStyle,
                  ]}
                />
              ) : null}
              <Animated.View
                accessibilityElementsHidden={!isExpanded}
                pointerEvents={isExpanded ? "auto" : "none"}
                style={[
                  {
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                  },
                  searchAnimatedStyle,
                ]}
              >
                <LBox justifyContent="center">
                  <LInput
                    accessibilityLabel="Find playlist"
                    autoCapitalize="none"
                    autoCorrect={false}
                    onChangeText={setSearchTerm}
                    placeholder="Find playlist"
                    placeholderTextColor={theme.colors.blackTranslucent50}
                    returnKeyType="search"
                    style={{
                      backgroundColor: theme.colors.white,
                      borderRadius: theme.borderRadii["m-l"],
                      color: theme.colors.black,
                      height: 40,
                      paddingLeft: 44,
                      paddingRight: searchTerm ? 44 : theme.spacing.s3,
                    }}
                    testID="add-to-playlist-search"
                    value={searchTerm}
                    variant="searchBarSmall"
                  />
                  <LIcon
                    colors={{ color: theme.colors.greyText }}
                    left={theme.spacing.s3}
                    name="search"
                    position="absolute"
                    size={22}
                  />
                  {searchTerm ? (
                    <LPressable
                      accessibilityLabel="Clear playlist search"
                      onPress={() => setSearchTerm("")}
                      sentryEventName="add_to_playlist_clear_search"
                      style={{
                        position: "absolute",
                        right: theme.spacing.s3,
                      }}
                    >
                      <LIcon name="circle_cross" size={22} />
                    </LPressable>
                  ) : null}
                </LBox>
              </Animated.View>
            </Animated.View>
          </>
        }
        renderItem={({ item }) => renderPlaylist(item)}
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
      />
    </LBox>
  );
};

export const AddToPlaylistModalSWMSheet = forwardRef<
  SheetAdapterRef,
  AddToPlaylistModalProps
>(function AddToPlaylistModalSWMSheet({ onSuccess, song }, ref) {
  const { height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [isExpanded, setIsExpanded] = useState(false);
  const compactHeight = Math.min(windowHeight * 0.42, COMPACT_SHEET_MAX_HEIGHT);
  const expandedHeight = Math.min(
    windowHeight * 0.9,
    windowHeight - insets.top - theme.spacing.s3,
  );

  return (
    <AppModalSheet
      detents={[0, compactHeight, expandedHeight]}
      expandedIndex={1}
      keyboardBehavior="inset"
      onIndexChange={(index) => setIsExpanded(index === 2)}
      ref={ref}
      showHandle
    >
      <AddToPlaylistModalSWM
        isExpanded={isExpanded}
        onSuccess={onSuccess}
        song={song}
      />
    </AppModalSheet>
  );
});

export default AddToPlaylistModalSWM;
